'use client';

import { useMemo } from 'react';
import type { Plan } from '@/core/domain';
import { PLAN_BILLING_PERIODS } from '@/core/domain';
import {
  buildChoosePlanDualLayoutModel,
  hasAnyPremiumPlan,
  type ChoosePlanDualLayoutModel,
} from '@/features/plans/presentation/utils/choose-plan-dual-layout';

export interface UseChoosePlanScreenDataInput {
  plans: Plan[];
  loading: boolean;
  error: string | null;
}

export interface UseChoosePlanScreenDataResult {
  dualModel: ChoosePlanDualLayoutModel;
  showMonthlyHint: boolean;
  showListLoading: boolean;
  showEmptyState: boolean;
}

export function useChoosePlanScreenData({
  plans,
  loading,
  error,
}: UseChoosePlanScreenDataInput): UseChoosePlanScreenDataResult {
  const dualModel = useMemo(() => buildChoosePlanDualLayoutModel(plans), [plans]);

  const showMonthlyHint = useMemo(() => {
    const paid = plans.find((p) => p.is_active && p.kind === 'paid');
    if (!paid?.billing_variants) return false;
    const hasMonthly = paid.billing_variants.monthly != null;
    const hasLongPeriod = PLAN_BILLING_PERIODS.some(
      (p) => p !== 'monthly' && paid.billing_variants?.[p] != null
    );
    return hasLongPeriod && !hasMonthly;
  }, [plans]);

  const hasContent = Boolean(dualModel.freePlan) || hasAnyPremiumPlan(dualModel);
  const showListLoading = loading && !hasContent && error == null;
  const showEmptyState = !loading && error == null && !hasContent;

  return { dualModel, showMonthlyHint, showListLoading, showEmptyState };
}
